import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { X, ArrowRight } from "lucide-react";
import { api, is429, retryAfterSec } from "@/lib/api";
import { SMS_MARKETING_LABEL, SMS_TRANSACTIONAL_LABEL, SMS_PHONE_DISCLOSURE } from "@/config/smsConsent";

const DISMISS_KEY = "bcu_waitlist_dismissed_v1";

export default function WaitlistBanner() {
  const [dismissed, setDismissed] = useState(false);
  const [isFull, setIsFull] = useState(false);
  const [remaining, setRemaining] = useState(null);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", city: "", state: "", notes: "",
    sms_marketing_consent: false, sms_transactional_consent: false,
    sms_consent_language_marketing: "", sms_consent_language_transactional: "",
    consent_privacy_terms: false,
    hp_field: "",
  });
  const submitBtnRef = useRef(null);
  const cooldownTimerRef = useRef(null);

  useEffect(() => () => { if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current); }, []);

  useEffect(() => {
    try { if (sessionStorage.getItem(DISMISS_KEY)) setDismissed(true); } catch { /* ignore */ }
    (async () => {
      try {
        const { data } = await api.get("/seats/status");
        setIsFull(!!data?.is_full);
        setRemaining(typeof data?.seats_remaining === "number" ? data.seats_remaining : null);
      } catch { /* ignore */ }
    })();
  }, []);

  const dismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email) { toast.error("Name and email are required."); return; }
    if (!form.consent_privacy_terms) { toast.error("Please agree to the Privacy Policy and Terms & Conditions."); return; }
    setSubmitting(true);
    if (cooldownTimerRef.current) { clearTimeout(cooldownTimerRef.current); cooldownTimerRef.current = null; }
    try {
      await api.post("/waitlist", form);
      toast.success("You're on the waitlist. We'll be in touch about the next cohort.");
      setOpen(false);
      dismiss();
    } catch (err) {
      if (is429(err)) {
        const sec = retryAfterSec(err);
        // Preserve entered form fields; only hold the submit button.
        toast.error("Too many attempts were received. Please wait a moment and try again.", { id: "rl-waitlist", duration: 8000 });
        cooldownTimerRef.current = setTimeout(() => {
          setSubmitting(false);
          cooldownTimerRef.current = null;
          try { submitBtnRef.current?.focus(); } catch { /* ignore */ }
        }, sec * 1000);
        return;
      }
      toast.error("Something went wrong. Please try again.");
    } finally {
      if (!cooldownTimerRef.current) setSubmitting(false);
    }
  };

  if (dismissed || !isFull) return null;

  return (
    <>
      <div data-testid="waitlist-banner" className="fixed bottom-0 inset-x-0 z-40 bg-black/95 backdrop-blur-md border-t border-[#D9A441]/40">
        <div className="bcu-container-wide py-4 flex items-center gap-4 flex-wrap">
          <div className="text-[11px] tracking-[0.22em] uppercase text-[#D9A441] font-semibold">Cohort Update</div>
          <div className="text-[15px] text-white flex-1 min-w-[240px]">
            The current cohort of <strong className="text-[#D9A441]">15 seats is full.</strong> Join the waitlist for the next cohort.
          </div>
          <button onClick={() => setOpen(true)} data-testid="waitlist-open-btn" className="bcu-btn-primary text-[13px] tracking-[0.10em] uppercase px-4 py-2.5">Join the waitlist <ArrowRight className="h-4 w-4" /></button>
          <button onClick={dismiss} data-testid="waitlist-dismiss" aria-label="Dismiss" className="text-[#B5B5B5] hover:text-white p-2"><X className="h-4 w-4" /></button>
        </div>
      </div>

      {open && (
        <div data-testid="waitlist-modal" className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/80">
          <div className="w-full max-w-lg bg-[#0D0D0D] border border-[#D9A441]/40 rounded-md p-8 relative">
            <button onClick={() => setOpen(false)} aria-label="Close" className="absolute top-4 right-4 text-[#B5B5B5] hover:text-white p-1"><X className="h-5 w-5" /></button>
            <div className="text-[11px] tracking-[0.24em] uppercase text-[#D9A441] font-semibold">Waitlist · Next Cohort</div>
            <h3 className="mt-3 font-display text-3xl md:text-4xl text-white font-medium leading-tight">Reserve your place for the next cohort.</h3>
            <p className="mt-3 text-[15px] text-[#B5B5B5]">This does not obligate you to apply. We'll contact you when applications open and only use your details to keep you informed about Bounce City University.</p>
            <form className="mt-6 grid gap-4" onSubmit={submit}>
              <input type="text" placeholder="Full name" required data-testid="wl-name" className="bcu-input" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
              <input type="email" placeholder="Email" required data-testid="wl-email" className="bcu-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <input type="tel" placeholder="Phone (optional)" data-testid="wl-phone" className="bcu-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <input type="text" placeholder="City" data-testid="wl-city" className="bcu-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                <input type="text" placeholder="State" data-testid="wl-state" className="bcu-input" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />
              </div>
              <textarea placeholder="Anything you want us to know (optional)" data-testid="wl-notes" rows="3" className="bcu-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              <input type="text" tabIndex="-1" autoComplete="off" className="hidden" value={form.hp_field} onChange={(e) => setForm({ ...form, hp_field: e.target.value })} />

              {/* A2P 10DLC — independent, optional SMS consents. */}
              <div className="border border-[rgba(255,255,255,0.10)] rounded-md p-4 bg-[#0A0A0A]">
                <div className="text-[10px] tracking-[0.22em] uppercase text-[#D9A441] font-bold">SMS Preferences</div>
                <p className="mt-2 text-[11px] text-[#7A8296] leading-relaxed">{SMS_PHONE_DISCLOSURE}</p>
                <label className="mt-3 flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="wl-sms-transactional"
                    checked={!!form.sms_transactional_consent}
                    onChange={(e) => setForm((d) => ({
                      ...d,
                      sms_transactional_consent: e.target.checked,
                      sms_consent_language_transactional: e.target.checked ? SMS_TRANSACTIONAL_LABEL : "",
                    }))}
                    className="mt-1 h-4 w-4 accent-[#D9A441]"
                  />
                  <span className="text-[12px] text-[#C7CFDD] leading-relaxed">{SMS_TRANSACTIONAL_LABEL}</span>
                </label>
                <label className="mt-3 flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="wl-sms-marketing"
                    checked={!!form.sms_marketing_consent}
                    onChange={(e) => setForm((d) => ({
                      ...d,
                      sms_marketing_consent: e.target.checked,
                      sms_consent_language_marketing: e.target.checked ? SMS_MARKETING_LABEL : "",
                    }))}
                    className="mt-1 h-4 w-4 accent-[#D9A441]"
                  />
                  <span className="text-[12px] text-[#C7CFDD] leading-relaxed">{SMS_MARKETING_LABEL}</span>
                </label>
              </div>

              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="wl-consent-privacy-terms"
                  checked={!!form.consent_privacy_terms}
                  onChange={(e) => setForm((d) => ({ ...d, consent_privacy_terms: e.target.checked }))}
                  className="mt-1 h-4 w-4 accent-[#D9A441]"
                />
                <span className="text-[12px] text-[#C7CFDD] leading-relaxed">
                  I agree to the <Link to="/privacy" className="underline hover:text-white">Privacy Policy</Link> and <Link to="/terms" className="underline hover:text-white">Terms &amp; Conditions</Link>. <span className="text-[#D9A441]">*</span>
                </span>
              </label>

              <button ref={submitBtnRef} type="submit" disabled={submitting} data-testid="wl-submit" className="bcu-btn-primary text-[14px] tracking-[0.12em] uppercase">
                {submitting ? "Submitting…" : "Join the waitlist"} <ArrowRight className="h-4 w-4" />
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
